import {
  PORTFOLIO_CLEANY,
  PORTFOLIO_SPECTACULAR,
  PORTFOLIO_PRECLOSE,
  PORTFOLIO_ADMACHINE,
} from "@/lib/optimizedImages";

interface ClientSite {
  id: string;
  name: string;
  url: string;
  fullUrl: string;
  image: string;
  title: string;
  description: string;
  desktopClass: string;
}

const clientSites: ClientSite[] = [
  {
    id: "cleany",
    name: "Cleany Canada",
    url: "cleany.ca",
    fullUrl: "https://cleany.ca/",
    image: PORTFOLIO_CLEANY,
    title: "Franchise Booking System",
    description: "Lead capture + automated dispatch",
    desktopClass: "lg:col-start-1 lg:row-start-1 lg:-rotate-1 lg:z-20",
  },
  {
    id: "spectacular",
    name: "Spectacular Academy",
    url: "spectacularacademy.com",
    fullUrl: "https://spectacularacademy.com/",
    image: PORTFOLIO_SPECTACULAR,
    title: "High-Ticket Funnel & Community",
    description: "Event registration + student pipeline",
    desktopClass:
      "lg:col-start-2 lg:row-start-1 lg:rotate-2 lg:z-30 lg:-mt-4 lg:-ml-6",
  },
  {
    id: "preclose",
    name: "Preclose AI",
    url: "preclose.ai",
    fullUrl: "https://preclose.ai/",
    image: PORTFOLIO_PRECLOSE,
    title: "Post-Click Funnel Architecture",
    description: "Inbound conversion + sales nurture",
    desktopClass:
      "lg:col-start-1 lg:row-start-2 lg:-rotate-1 lg:z-20 lg:mt-6 lg:ml-1",
  },
  {
    id: "admachine",
    name: "AI Ad Machine",
    url: "admachine.dev",
    fullUrl: "https://admachine.dev/",
    image: PORTFOLIO_ADMACHINE,
    title: "Real Estate Ad Engine & CRM",
    description: "Automated ad workflows + lead pipeline",
    desktopClass:
      "lg:col-start-2 lg:row-start-2 lg:rotate-1 lg:z-30 lg:mt-3 lg:-ml-6",
  },
];

export function EliteClientRoster() {
  return (
    <section className="relative w-full overflow-hidden bg-white text-[#082B5A] pt-24 pb-20 md:pt-32 md:pb-28 lg:pt-36 lg:pb-32">
      {/* Decorative ambient gradients */}
      <div className="pointer-events-none absolute -top-40 right-[-8%] h-[760px] w-[760px] rounded-full bg-[#119EF8]/12 blur-[140px]" />
      <div className="pointer-events-none absolute top-[28%] -left-40 h-[600px] w-[600px] rounded-full bg-[#35B8FB]/10 blur-[130px]" />
      <div className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 h-[360px] w-full max-w-[1400px] bg-gradient-to-t from-[#EBF5FF] to-transparent opacity-80" />

      {/* Main Container */}
      <div className="relative mx-auto max-w-[1460px] px-6 sm:px-8 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT COLUMN: Editorial Headline & Copy */}
          <div className="lg:col-span-5 xl:col-span-5 relative z-10 lg:pt-6 lg:sticky lg:top-28">
            {/* Small green dot + Eyebrow */}
            <div className="flex items-center gap-2 mb-4 md:mb-5">
              <span className="h-2 w-2 rounded-full bg-[#35F35A] shadow-[0_0_8px_#35F35A]" />
              <span className="font-heading text-[11px] md:text-[12px] font-bold uppercase tracking-[0.2em] text-[#0A4B8A]">
                THE ELITE ROSTER
              </span>
            </div>

            {/* Main Headline — larger, tight block */}
            <h2 className="font-heading font-extrabold text-[#082B5A] text-[clamp(42px,3.8vw,64px)] leading-[0.92] tracking-[-0.045em] mb-6">
              Trusted by
              <br />
              Companies That
              <br />
              <span className="text-[#0A8FF5]">Can’t Afford to Break.</span>
            </h2>

            {/* Supporting Paragraph */}
            <p className="font-sans text-[15px] sm:text-[16px] md:text-[17px] leading-[1.55] text-[#435B78] max-w-[460px]">
              From Canadian{" "}
              <strong className="font-semibold text-[#082B5A]">
                enterprise franchises
              </strong>{" "}
              to{" "}
              <strong className="font-semibold text-[#082B5A]">
                high-volume
              </strong>{" "}
              US ad agencies, here are a few of the{" "}
              <strong className="font-semibold text-[#082B5A] underline decoration-[#0A8FF5] decoration-2 underline-offset-4">
                300+
              </strong>{" "}
              live systems and workflows I architected to run without breaking.
            </p>

            {/* Decorative accent — small editorial mark */}
            <div className="hidden lg:flex items-center gap-1.5 mt-10 text-[#35F35A]">
              <span className="inline-block w-4 h-1 rounded-full bg-[#35F35A] rotate-45 transform" />
              <span className="inline-block w-4 h-1 rounded-full bg-[#35F35A] -rotate-45 transform -ml-2" />
            </div>
          </div>

          {/* RIGHT COLUMN: Overlapping Card Collage & Large Backdrop Word */}
          <div className="lg:col-span-7 xl:col-span-7 relative">
            {/* Giant Pale PROOF Text — bigger, brighter */}
            <div
              aria-hidden="true"
              className="pointer-events-none select-none absolute -top-20 md:-top-28 right-[-2%] lg:-right-6 font-heading font-black text-[150px] sm:text-[210px] md:text-[270px] lg:text-[290px] xl:text-[330px] leading-none tracking-[-0.07em] text-[#0A8FF5]/[0.085] z-0"
            >
              PROOF
            </div>

            {/* Blue Abstract Background Shape — large organic flowing field */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-6 -left-2 md:left-8 w-[92%] h-[88%] rounded-[42%_58%_55%_45%/50%_45%_55%_50%] bg-gradient-to-tr from-[#087CEF]/30 via-[#17A6F8]/35 to-[#48C7FF]/45 blur-[40px] md:blur-[64px] z-0 opacity-80"
            />

            {/* Handwritten "Built to run." Annotation with Arrow */}
            <div
              aria-hidden="true"
              className="pointer-events-none hidden sm:flex items-center gap-2 absolute -top-8 md:-top-12 right-2 lg:right-4 z-30"
            >
              <span className="font-script text-[#0A7DEB] text-[26px] md:text-[32px] rotate-[-6deg] font-semibold tracking-wide">
                Built to run.
              </span>
              <svg
                className="w-9 h-9 text-[#0A7DEB] -rotate-12 translate-y-2 opacity-80"
                viewBox="0 0 40 40"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 25 C15 35, 28 30, 32 10" />
                <path d="M26 8 L33 9 L31 16" />
              </svg>
            </div>

            {/* 4 Overlapping Cards — balanced vertical spacing so labels never overlap */}
            <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 gap-10 sm:gap-8 md:gap-9 pt-6 sm:pt-8">
              {clientSites.map((site) => (
                <div
                  key={site.id}
                  className={`relative flex flex-col ${site.desktopClass}`}
                >
                  {/* Clickable Card Anchor — link intact */}
                  <a
                    href={site.fullUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Visit live client deployment for ${site.name} at ${site.url}`}
                    className="group relative block w-full rounded-[20px] md:rounded-[24px] bg-white p-3 md:p-3.5 transition-all duration-300 ease-out hover:-translate-y-2 hover:scale-[1.015] border border-[#082B5A]/10 shadow-[0_22px_55px_rgba(8,43,90,0.14)] hover:shadow-[0_28px_70px_rgba(8,43,90,0.22)]"
                  >
                    {/* Browser top-bar */}
                    <div className="flex items-center justify-between px-2 pb-2.5 pt-1 border-b border-[#082B5A]/5 mb-2">
                      <div className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F56]" />
                        <span className="h-2.5 w-2.5 rounded-full bg-[#FFBD2E]" />
                        <span className="h-2.5 w-2.5 rounded-full bg-[#27C93F]" />
                      </div>
                      <span className="font-mono text-[10px] text-[#435B78]/70 truncate max-w-[170px]">
                        {site.url}
                      </span>
                      <div className="w-6" />
                    </div>

                    {/* Screenshot Frame — larger */}
                    <div className="relative aspect-[16/10] w-full overflow-hidden rounded-[14px] md:rounded-[16px] bg-[#F4F7FC]">
                      <img
                        src={site.image}
                        alt={`${site.name} live client deployment`}
                        width={400}
                        height={250}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                      />
                    </div>
                  </a>

                  {/* Floating Project Label — anchored, non-blocking */}
                  <div className="pointer-events-none relative -mt-6 sm:-mt-7 mx-3 sm:mx-4 z-40 rounded-[14px] md:rounded-[16px] bg-white px-4 py-3 shadow-[0_12px_28px_rgba(8,43,90,0.13)] border border-[#082B5A]/8 flex items-center justify-between gap-2">
                    <div className="flex items-start gap-2.5">
                      <span className="h-2 w-2 mt-1.5 shrink-0 rounded-full bg-[#35F35A] shadow-[0_0_6px_#35F35A]" />
                      <div>
                        <div className="font-heading font-bold text-[13px] sm:text-[14px] leading-tight text-[#082B5A]">
                          {site.title}
                        </div>
                        <div className="font-sans text-[11px] sm:text-[12px] text-[#5E7187] leading-tight mt-0.5">
                          {site.description}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
export default EliteClientRoster;
